'use client';

import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';

import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { postNotifyProduct } from '@/api/product';
import Wish from '@/components/Product/Wish';
import { toast } from '@/components/Toast';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';

import { EPlatform } from '@/utils/enums/platform-enum';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import appCommunicationManager from '@/utils/app-communication-manager';
import { randomFuncName } from '@/utils/tools';
import logger from '@/utils/client-logger';

// 商品数据接口
interface GoodItemProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: any[];
  promo_listed_status?: number;
  [key: string]: any;
}

interface GoodItemWapProps {
  product: GoodItemProduct;
  index: number;
  tabName?: string;
  deviceType?: EPlatform;
  onProductClick?: (product: GoodItemProduct, index: number) => void;
  onProductExp?: (product: GoodItemProduct, index: number) => void;
}

export default function GoodItemWap({
  product,
  index,
  tabName = '',
  deviceType = EPlatform.WEB,
  onProductClick,
  onProductExp,
}: GoodItemWapProps) {
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const t = useTranslations('product');
  const summerT = useTranslations('summerEdit' as any) as any;

  const [localPromoStatus, setLocalPromoStatus] = useState<number>(
    product.promo_listed_status || 3,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setShowLoginRegisterDialog = useStore((s) => s.setShowLoginRegisterDialog);
  const setToken = useStore((s) => s.setToken);
  const refreshAccountInfo = useStore((s) => s.refreshAccountInfo);

  useEffect(() => {
    setLocalPromoStatus(product.promo_listed_status || 3);
  }, [product.promo_listed_status]);

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onProductExp?.(product, index);
    }
  }, [inView, onProductExp, product, index]);

  // 判断是否售罄
  const isSoldOut = product.stock <= 0;

  const { trackNotifyMeClick, trackNotifyMeSubmit } = useFrenchSummerTracking();
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  useEffect(() => {
    // 当用户信息更新，且存在待订阅状态时，自动触发订阅
    if (userInfo?.email && pendingSubscribe) {
      const autoSubscribe = async () => {
        try {
          setIsSubmitting(true);
          await postNotifyProduct({
            email: userInfo.email,
            id: product.id as any,
            notice_type: 'promo_listed',
          });
          setLocalPromoStatus(2);
          trackNotifyMeSubmit(product, '商品版位', index, tabName);
        } catch {
          // 忽略错误
        } finally {
          setIsSubmitting(false);
          setPendingSubscribe(false);
        }
      };
      autoSubscribe();
    }
  }, [userInfo?.email, pendingSubscribe, product, trackNotifyMeSubmit, index, tabName]);

  const handleNotifyMe = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    trackNotifyMeClick(product, '商品版位', index, tabName);

    if (isSubmitting) return;

    if (!userInfo?.email) {
      setPendingSubscribe(true);
      if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
        try {
          const logincallbackFnName = randomFuncName('JumpToPage');
          (window as any)[logincallbackFnName] = async (val: any) => {
            if (val !== 'ClearToken') {
              setToken(val);
              setTimeout(async () => {
                await refreshAccountInfo();
              }, 100);
            }
          };
          const loginData = {
            RouteName: '/login',
            closedCurrentPage: false,
            backToken: true,
            Callback: logincallbackFnName,
          };
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, loginData);
        } catch (error) {
          logger.error('App登录跳转失败:', error);
        }
      } else {
        setShowLoginRegisterDialog(true);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      await postNotifyProduct({
        email: userInfo.email,
        id: product.id as any,
        notice_type: 'promo_listed',
      });
      setLocalPromoStatus(2);
      trackNotifyMeSubmit(product, '商品版位', index, tabName);
    } catch {
      // 忽略错误
    } finally {
      setIsSubmitting(false);
    }
  };

  // 获取商品图片
  const productImg = (item: GoodItemProduct) => {
    const list = item?.image?.product || [];
    return list[0] || '';
  };

  // 处理商品点击
  const toDetail = (item: GoodItemProduct, itemIndex: number) => {
    onProductClick?.(item, itemIndex);
  };

  return (
    <a
      href={`/goods-detail/${product.shop_sku}`}
      onClick={(e) => {
        // 如果是点击了按钮，则不需要阻止默认行为和冒泡（由按钮自身的事件处理）
        const target = e.target as HTMLElement;
        if (target.closest('button')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        toDetail(product, index);
        // 手动跳转，因为被 preventDefault 了
        if (product.shop_sku) {
          window.location.href = `/goods-detail/${product.shop_sku}`;
        }
      }}
    >
      <div
        ref={expRef}
        className="product-info bg-[#eeeeee] rounded-[4px] overflow-hidden relative w-full flex flex-col"
        style={{ aspectRatio: '182/240' }}
      >
        {/* Coming Soon Badge */}
        {(localPromoStatus === 1 || localPromoStatus === 2) && (
          <div className="absolute top-[10px] left-[10px] z-10 flex pointer-events-none">
            <span className="font-bs text-[10px] text-[#282828] leading-none">
              {summerT('coomingSoon')}
            </span>
          </div>
        )}

        {/* Wishlist Icon */}
        <Wish
          data={product as any}
          className="absolute top-[10px] right-[10px] z-10 flex"
          iconSize="14px"
        />

        {/* 图片容器 - 占据主要空间 */}
        <div className="image-wrapper flex-1 px-[16px] pt-[24px] pb-[8px] flex items-center justify-center overflow-hidden">
          <Image
            src={productImg(product)}
            alt={`${product.name || ''}`}
            width={600}
            height={600}
            className="black-friday-bottom-image max-w-full max-h-full object-contain mix-blend-multiply"
          />
        </div>

        {/* 商品信息区域 - 固定底部空间 */}
        <div
          className={`product-details pt-[4px] px-[12px] ${localPromoStatus === 1 || localPromoStatus === 2 ? 'pb-[10px]' : 'pb-[16px]'} flex-shrink-0 flex flex-col items-center w-full`}
        >
          {/* 商品名称 */}
          <div className="promotion-list-item-name text-[12px] text-[#1a1c1e] mb-[4px] text-center line-clamp-1 font-br w-full">
            {product.short_name || product.name}
          </div>

          {/* 价格信息 */}
          <div className="promotion-list-item-price flex justify-center items-center h-[18px]">
            {(() => {
              const p = priceInfo(userInfo, product, currency) || {};
              const now = formatPriceNumberWithSymbol(
                String(p?.sell_price || p?.price || '0'),
                currency,
              );
              const old =
                p && p.price && p.sell_price && p.sell_price !== p.price
                  ? formatPriceNumberWithSymbol(String(p.price), currency)
                  : '';
              return isSoldOut ? (
                <span className="sold-out-price text-[14px] text-[#999999] font-bb">{now}</span>
              ) : (
                <>
                  <span className="now-price notranslate text-[14px] text-[#1a1c1e] font-bb">
                    {now}
                  </span>
                  {old && (
                    <span className="old-price notranslate text-[12px] text-[#1a1c1e] line-through ml-[4px] font-br">
                      {old}
                    </span>
                  )}
                </>
              );
            })()}
          </div>

          {/* Notify Me / Subscribed Button */}
          {(localPromoStatus === 1 || localPromoStatus === 2) && (
            <div className="w-full h-[32px] mt-[12px]">
              {localPromoStatus === 1 ? (
                <button
                  className="w-full h-full bg-white text-[#282828] font-bs text-[12px] rounded-[4px] flex items-center justify-center gap-[4px] active:opacity-80 transition-opacity"
                  onClick={handleNotifyMe}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.0001 4.49994C7.17559 4.49994 3.25009 8.42544 3.25009 13.2499C3.25009 18.0744 7.17559 21.9999 12.0001 21.9999C16.8246 21.9999 20.7501 18.0744 20.7501 13.2499C20.7501 8.42544 16.8246 4.49994 12.0001 4.49994ZM12.0001 20.4999C8.00259 20.4999 4.75009 17.2474 4.75009 13.2499C4.75009 9.25244 8.00259 5.99994 12.0001 5.99994C15.9976 5.99994 19.2501 9.25244 19.2501 13.2499C19.2501 17.2474 15.9976 20.4999 12.0001 20.4999ZM21.7556 6.61944C20.7148 4.76689 19.136 3.27404 17.2281 2.33844C17.1396 2.29504 17.0435 2.26948 16.9452 2.26323C16.8469 2.25698 16.7483 2.27015 16.6551 2.30201C16.5618 2.33386 16.4758 2.38376 16.4018 2.44886C16.3279 2.51396 16.2675 2.593 16.2241 2.68144C16.1807 2.76989 16.1551 2.86601 16.1489 2.96434C16.1426 3.06266 16.1558 3.16125 16.1877 3.25448C16.2195 3.34771 16.2694 3.43375 16.3345 3.5077C16.3996 3.58164 16.4786 3.64204 16.5671 3.68544C18.2033 4.48774 19.5572 5.76808 20.4496 7.35694C20.4972 7.44417 20.5617 7.52107 20.6393 7.58316C20.7169 7.64525 20.8061 7.69129 20.9017 7.71861C20.9973 7.74593 21.0973 7.75399 21.196 7.74231C21.2947 7.73063 21.3901 7.69944 21.4766 7.65057C21.5632 7.6017 21.6392 7.53611 21.7001 7.45762C21.7611 7.37913 21.8059 7.28931 21.8318 7.19336C21.8578 7.09742 21.8644 6.99727 21.8513 6.89875C21.8382 6.80022 21.8057 6.70528 21.7556 6.61944ZM3.55059 7.35644C4.44298 5.76758 5.7969 4.48724 7.43309 3.68494C7.60619 3.59407 7.737 3.43923 7.79768 3.25338C7.85836 3.06753 7.84412 2.86533 7.75799 2.68982C7.67187 2.51431 7.52065 2.37933 7.33652 2.31362C7.15239 2.2479 6.94989 2.25662 6.77209 2.33794C4.86404 3.27326 3.28515 4.76618 2.24459 6.61894C2.19449 6.70478 2.16195 6.79972 2.14886 6.89825C2.13578 6.99677 2.1424 7.09692 2.16836 7.19286C2.19431 7.28881 2.23908 7.37863 2.30006 7.45712C2.36103 7.53561 2.437 7.6012 2.52355 7.65007C2.61009 7.69894 2.70549 7.73013 2.80419 7.74181C2.90289 7.75349 3.00293 7.74543 3.0985 7.71811C3.19406 7.69079 3.28324 7.64475 3.36085 7.58266C3.43846 7.52057 3.50296 7.44367 3.55059 7.35644Z"
                      fill="#282828"
                    />
                  </svg>
                  {summerT('notify')}
                </button>
              ) : (
                <button
                  className="w-full h-full bg-white text-[#282828]/50 font-bs text-[12px] rounded-[4px] flex items-center justify-center gap-[4px] cursor-not-allowed"
                  onClick={(e) => {
                    toast.info(summerT('subscribedToast'));
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.80569 17.9995C9.63245 18.0058 9.46252 17.9507 9.32568 17.8439L4.20558 12.8003C4.14062 12.7382 4.0889 12.6635 4.05356 12.5808C4.01822 12.498 4 12.4089 4 12.3188C4 12.2288 4.01822 12.1397 4.05356 12.0569C4.0889 11.9742 4.14062 11.8995 4.20558 11.8374C4.2675 11.7721 4.34197 11.7202 4.42448 11.6847C4.50699 11.6492 4.59582 11.6309 4.68559 11.6309C4.77535 11.6309 4.86418 11.6492 4.94669 11.6847C5.0292 11.7202 5.10368 11.7721 5.1656 11.8374L9.78269 16.4221L18.8339 7.20649C18.8958 7.14124 18.9703 7.08929 19.0528 7.0538C19.1353 7.0183 19.2241 7 19.3139 7C19.4037 7 19.4925 7.0183 19.575 7.0538C19.6575 7.08929 19.732 7.14124 19.7939 7.20649C19.859 7.26859 19.9109 7.34334 19.9463 7.4262C19.9817 7.50906 20 7.59829 20 7.68846C20 7.77863 19.9817 7.86786 19.9463 7.95072C19.9109 8.03357 19.859 8.10832 19.7939 8.17042L10.2857 17.7977C10.2245 17.8635 10.15 17.9155 10.0673 17.9503C9.98453 17.9851 9.89537 18.0019 9.80569 17.9995Z"
                      fill="#282828"
                      fillOpacity="0.5"
                    />
                  </svg>
                  {summerT('subscribed')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 售罄遮罩 (仅在 promoStatus === 3 或没有该字段时显示常规的售罄) */}
        {isSoldOut && (!product.promo_listed_status || product.promo_listed_status === 3) && (
          <div className="absolute inset-0 bg-white/40 rounded-[4px] cursor-default z-20">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[27%] aspect-square rounded-full bg-black/60 text-center">
              <div
                className="text-white whitespace-pre-wrap text-[12px]"
                dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
              />
            </span>
          </div>
        )}
      </div>
    </a>
  );
}
